'use client'

import Image from 'next/image'
import Link from 'next/link'
import bigLogo from '../../../../public/img/gratialogo_horizontal_color.png'
import { Suspense, useMemo } from 'react'
import { useQueryState } from 'nuqs'
import Divider from '@/components/Divider'
import MagicLinkSignInForm from './MagicLinkSignInForm'
import BadgeWaterfall from './BadgeWaterfall'
import { signInPhoneSearchParams } from '@/lib/magic-link-client'

const ERROR_MESSAGES: Record<string, string> = {
  MagicLinkInvalid: 'This sign-in link is invalid or has expired. Please request a new link below.',
}

function getErrorMessage(errorCode: string | null): string | null {
  if (!errorCode) return null
  return ERROR_MESSAGES[errorCode] ?? 'Something went wrong. Please try again.'
}

type SignInClientProps = {
  signInTechnicalDifficultiesBanner: boolean
  errorCode: string | null
}

export default function SignInClient({ signInTechnicalDifficultiesBanner, errorCode }: SignInClientProps) {
  const [codeSent] = useQueryState('codeSent', signInPhoneSearchParams.codeSent)
  const errorMessage = useMemo(() => getErrorMessage(errorCode), [errorCode])
  const subtext = codeSent ? null : 'Sign in with your email or phone number.'

  return (
    <div className="relative min-h-screen w-full bg-gradient-to-b from-blue-50 to-white px-4 py-8">
      <BadgeWaterfall />
      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-5xl flex-col justify-center">
        <div className="mb-6 flex flex-col items-center">
          <Image src={bigLogo} priority alt="Gratia logo" width={250} className="h-auto" />
        </div>

        <div className="border-blue-100 mx-auto w-full max-w-sm rounded-2xl border bg-white p-8 shadow-xl">
          {signInTechnicalDifficultiesBanner && (
            <div className="mb-5 rounded-md border-2 border-black-100 bg-yellow-light p-3 text-sm">
              Some of our Users are unable to login right now. We are aware of the problem and are working quickly to
              fix it. Please try again later.
            </div>
          )}

          {errorMessage && (
            <div role="alert" className="text-black mb-5 rounded-md border-2 border-red-50 p-3 text-sm">
              Error: {errorMessage}
            </div>
          )}

          <div className="mb-5 text-center">
            <h1 aria-label="Sign in to your account" className="text-2xl font-bold leading-tight text-gray-800">
              Welcome back
            </h1>
            {subtext && <p className="mt-2 text-sm text-gray-600">{subtext}</p>}
          </div>

          <Suspense>
            <MagicLinkSignInForm />
          </Suspense>

          {!codeSent && (
            <>
              <Divider />
              <p className="text-center text-sm text-gray-600">
                Don&apos;t have an account?{' '}
                <Link
                  className="cursor-pointer text-blue-50 underline hover:text-blue-medium"
                  href="/new-user-registration"
                >
                  Register
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
